import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList,
} from 'recharts';
import DashboardCard from './DashboardCard';
import { PrivacyNote } from '../styles';

// NineBoxDistribution — süreç seçimli (bkz. TalentDashboardPage'in FilterRow'u).
// 9 sabit hücre HER ZAMAN döner (boş hücreler Count=0 ile, bkz. backend
// NineBoxCellKeys.AllKeys yorumu) — X ekseni zaten hücre kimliğini taşıdığından
// TEK renk (theme.colors.primary.main) kullanılır; 9 kategoriye kategorik renk
// ATANMAZ (dataviz kuralı: "a 9th series is never a generated hue"). Yatay
// çubuk grafik uzun hücre adlarını (ör. "Uzman / Usta") dikey eksen rotasyonu
// olmadan okunur kılar.
export default function NineBoxDistributionCard({ distribution, loading }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const totalPlaced = distribution?.totalPlaced || 0;

  // TD-17 — `suppressed`: hücrede yerleşim VAR ama asgari hücre büyüklüğünün altında
  // olduğu için sayısı hiç gönderilmedi. `count === 0 && !suppressed` ile (gerçekten boş
  // hücre) KARIŞTIRILAMAZ; ikisini de "0" göstermek "bu hücrede kimse yok" yanılgısı
  // üretirdi. totalPlaced de GÖRÜNEN toplamdır (bkz. backend TalentCellSuppression).
  const suppressedCount = (distribution?.cells || []).filter((c) => c.suppressed).length;

  const chartData = useMemo(() => (distribution?.cells || []).map((c) => ({
    cellKey: c.cellKey,
    name: t(`performance.ninebox.cell.${c.cellKey}`),
    count: c.count,
  })), [distribution, t]);

  return (
    <DashboardCard
      title={t('talent.dashboard.nineBox.title')}
      meta={distribution?.processName}
      span={8}
      spanMd={6}
      loading={loading}
      empty={totalPlaced === 0 && suppressedCount === 0}
      emptyText={t('talent.dashboard.nineBox.empty')}
    >
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData} layout="vertical" margin={{
          top: 5, right: 30, left: 0, bottom: 5,
        }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.surface.border} horizontal={false} />
          <XAxis type="number" hide allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="name"
            width={130}
            tick={{ fontSize: 11, fill: theme.colors.text.muted }}
            axisLine={{ stroke: theme.colors.surface.border }}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: 'rgba(2, 145, 239, 0.05)' }}
            formatter={(value) => [value, t('performance.report.col.count')]}
          />
          <Bar dataKey="count" name={t('performance.report.col.count')} radius={[0, 4, 4, 0]} barSize={16} fill={theme.colors.primary.main}>
            <LabelList
              dataKey="count"
              position="right"
              style={{ fontSize: 11, fontWeight: 700, fill: theme.colors.text.primary }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {suppressedCount > 0 && (
        <PrivacyNote data-role="ninebox-privacy-note">
          {t('talent.dashboard.nineBox.suppressedHint', { count: suppressedCount })}
        </PrivacyNote>
      )}
    </DashboardCard>
  );
}
